/**
 * Admin Plan Management page.
 *
 * Read-only dashboard for plans/entitlements: a summary of how tiers are
 * distributed, the list of current plans (paid tiers first), and the archive
 * of ended subscriptions (superseded / expired) retained for analysis.
 *
 * Route: `/admin/plans`
 * Requires authentication: Yes (admin only)
 *
 * @module AdminPlansPage
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CreditCard, Loader2, RefreshCcw } from 'lucide-react';
import {
  getActivePlans,
  getPlanHistory,
  getPlanSummary,
  type AdminActivePlansResponse,
  type AdminPlanHistoryResponse,
  type AdminPlanSummary,
} from '@/lib/adminPlanApi';

const PAGE_SIZE = 25;
const EXPIRING_SOON_MS = 7 * 24 * 60 * 60 * 1000;

/** Display labels for tiers (presentational only — limits live on the backend). */
const TIER_LABELS: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  'exam-ready': 'Exam-Ready',
  'full-prep': 'Full Prep',
};

const tierLabel = (planId: string): string => TIER_LABELS[planId] ?? planId;

const formatDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString() : '—';

const formatDateTime = (value?: string | null): string =>
  value ? new Date(value).toLocaleString() : '—';

function TierBadge({ planId }: { planId: string }) {
  const variant = planId === 'free' ? 'outline' : 'default';
  return (
    <Badge variant={variant} className="font-ui">
      {tierLabel(planId)}
    </Badge>
  );
}

function StatusBadge({
  planId,
  isExpired,
  expiresAt,
}: {
  planId: string;
  isExpired: boolean;
  expiresAt?: string | null;
}) {
  if (isExpired) {
    return (
      <Badge variant="outline" className="font-ui border-destructive/50 text-destructive">
        Expired
      </Badge>
    );
  }
  if (planId === 'free') {
    return (
      <Badge variant="outline" className="font-ui text-muted-foreground">
        Free
      </Badge>
    );
  }
  if (expiresAt && new Date(expiresAt).getTime() - Date.now() < EXPIRING_SOON_MS) {
    return (
      <Badge variant="secondary" className="font-ui text-amber-700">
        Expiring soon
      </Badge>
    );
  }
  return (
    <Badge variant="default" className="font-ui">
      Active
    </Badge>
  );
}

function ReasonBadge({ reason }: { reason: string }) {
  const isExpired = reason === 'expired';
  return (
    <Badge
      variant="outline"
      className={isExpired ? 'font-ui border-destructive/50 text-destructive' : 'font-ui'}
    >
      {isExpired ? 'Expired' : 'Superseded'}
    </Badge>
  );
}

interface PagerProps {
  page: number;
  pageSize: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  loading?: boolean;
}

function Pager({ page, pageSize, total, onPrev, onNext, loading }: PagerProps) {
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <div className="flex items-center justify-between pt-2">
      <p className="font-ui text-xs text-muted-foreground">
        {total === 0 ? 'No records' : `Showing ${from}–${to} of ${total}`}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onPrev} disabled={loading || page === 0}>
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={loading || (page + 1) * pageSize >= total}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="font-ui text-sm text-muted-foreground">{label}</p>
        <p className={`font-ui text-2xl font-bold ${accent ?? 'text-foreground'}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

export default function AdminPlansPage() {
  const [summary, setSummary] = useState<AdminPlanSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [paidOnly, setPaidOnly] = useState(true);
  const [activePage, setActivePage] = useState(0);
  const [active, setActive] = useState<AdminActivePlansResponse>({ items: [], total: 0 });
  const [loadingActive, setLoadingActive] = useState(false);

  const [historyPage, setHistoryPage] = useState(0);
  const [history, setHistory] = useState<AdminPlanHistoryResponse>({ items: [], total: 0 });
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      setSummary(await getPlanSummary());
    } catch {
      /* toast shown in api client */
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const fetchActive = useCallback(async () => {
    setLoadingActive(true);
    try {
      setActive(await getActivePlans({ paidOnly, limit: PAGE_SIZE, offset: activePage * PAGE_SIZE }));
    } catch {
      /* toast shown in api client */
    } finally {
      setLoadingActive(false);
    }
  }, [paidOnly, activePage]);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      setHistory(await getPlanHistory({ limit: PAGE_SIZE, offset: historyPage * PAGE_SIZE }));
    } catch {
      /* toast shown in api client */
    } finally {
      setLoadingHistory(false);
    }
  }, [historyPage]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchActive();
  }, [fetchActive]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Reset to first page when the paid-only filter changes.
  useEffect(() => {
    setActivePage(0);
  }, [paidOnly]);

  const refreshAll = () => {
    fetchSummary();
    fetchActive();
    fetchHistory();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg font-ui">
                <CreditCard className="h-5 w-5 text-primary" />
                Plan Management
              </CardTitle>
              <CardDescription className="font-ui">
                Current entitlements and the archive of ended subscriptions, for visibility
                and analysis. Grant or revoke access through the Activation tab.
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={refreshAll} disabled={loadingSummary}>
              {loadingSummary ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <RefreshCcw className="h-4 w-4 mr-1" />
                  <span className="font-ui">Refresh</span>
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Key metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard
              label="Active paid"
              value={summary?.active_paid ?? 0}
              accent="text-primary"
            />
            <SummaryCard
              label="Expiring soon"
              value={summary?.expiring_soon ?? 0}
              accent="text-amber-600"
            />
            <SummaryCard
              label="Expired (pending)"
              value={summary?.expired_pending ?? 0}
              accent="text-destructive"
            />
            <SummaryCard label="Archived total" value={summary?.history_total ?? 0} />
          </div>

          {/* Tier distribution */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <SummaryCard label="Total users" value={summary?.total_users ?? 0} />
            <SummaryCard label="Free" value={summary?.free ?? 0} />
            <SummaryCard label="Starter" value={summary?.starter ?? 0} />
            <SummaryCard label="Exam-Ready" value={summary?.exam_ready ?? 0} />
            <SummaryCard label="Full Prep" value={summary?.full_prep ?? 0} />
          </div>

          {/* Active / History tables */}
          <Tabs defaultValue="active" className="w-full">
            <TabsList>
              <TabsTrigger value="active" className="font-ui">
                Active plans
              </TabsTrigger>
              <TabsTrigger value="history" className="font-ui">
                History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-3 pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch id="paid-only" checked={paidOnly} onCheckedChange={setPaidOnly} />
                  <Label htmlFor="paid-only" className="font-ui text-sm">
                    Paid plans only
                  </Label>
                </div>
                {loadingActive && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-ui">User</TableHead>
                      <TableHead className="font-ui">Tier</TableHead>
                      <TableHead className="font-ui">Level</TableHead>
                      <TableHead className="font-ui">Status</TableHead>
                      <TableHead className="font-ui">Activated</TableHead>
                      <TableHead className="font-ui">Expires</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {active.items.length === 0 && !loadingActive ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground font-ui py-6">
                          No plans to show.
                        </TableCell>
                      </TableRow>
                    ) : (
                      active.items.map((item) => (
                        <TableRow key={item.user_id}>
                          <TableCell className="font-ui">
                            <div className="font-medium">{item.username}</div>
                            {item.email && (
                              <div className="text-xs text-muted-foreground">{item.email}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <TierBadge planId={item.plan_id} />
                          </TableCell>
                          <TableCell className="font-ui text-sm">
                            {item.chosen_level ?? '—'}
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              planId={item.plan_id}
                              isExpired={item.is_expired}
                              expiresAt={item.expires_at}
                            />
                          </TableCell>
                          <TableCell className="font-ui text-sm text-muted-foreground">
                            {formatDate(item.activated_at)}
                          </TableCell>
                          <TableCell className="font-ui text-sm text-muted-foreground">
                            {formatDate(item.expires_at)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <Pager
                page={activePage}
                pageSize={PAGE_SIZE}
                total={active.total}
                loading={loadingActive}
                onPrev={() => setActivePage((p) => Math.max(0, p - 1))}
                onNext={() => setActivePage((p) => p + 1)}
              />
            </TabsContent>

            <TabsContent value="history" className="space-y-3 pt-4">
              {loadingHistory && (
                <div className="flex justify-end">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-ui">User</TableHead>
                      <TableHead className="font-ui">Tier</TableHead>
                      <TableHead className="font-ui">Level</TableHead>
                      <TableHead className="font-ui">Reason</TableHead>
                      <TableHead className="font-ui">Activated</TableHead>
                      <TableHead className="font-ui">Ended</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.items.length === 0 && !loadingHistory ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground font-ui py-6">
                          No archived subscriptions yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      history.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-ui font-medium">{item.username}</TableCell>
                          <TableCell>
                            <TierBadge planId={item.plan_id} />
                          </TableCell>
                          <TableCell className="font-ui text-sm">
                            {item.chosen_level ?? '—'}
                          </TableCell>
                          <TableCell>
                            <ReasonBadge reason={item.ended_reason} />
                          </TableCell>
                          <TableCell className="font-ui text-sm text-muted-foreground">
                            {formatDate(item.activated_at)}
                          </TableCell>
                          <TableCell className="font-ui text-sm text-muted-foreground">
                            {formatDateTime(item.ended_at)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <Pager
                page={historyPage}
                pageSize={PAGE_SIZE}
                total={history.total}
                loading={loadingHistory}
                onPrev={() => setHistoryPage((p) => Math.max(0, p - 1))}
                onNext={() => setHistoryPage((p) => p + 1)}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
